import { NgForOf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TUI_DOC_ICONS } from '@taiga-ui/addon-doc/tokens';
import { TuiAlertService, TuiButton } from '@taiga-ui/core';
import { TuiDataList } from '@taiga-ui/core/components/data-list';
import { tuiScrollbarOptionsProvider } from '@taiga-ui/core/components/scrollbar';
import { TuiTextfield } from '@taiga-ui/core/components/textfield';
import { TuiFlagPipe } from '@taiga-ui/core/pipes/flag';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n/utils';
import { TuiButtonSelect } from '@taiga-ui/kit/directives/button-select';
import { WA_WINDOW } from '@ng-web-apis/common';
import { PolymorpheusTemplate } from '@taiga-ui/polymorpheus';
import { UserService } from '../../services';
import { TuiOptGroup } from '@taiga-ui/core/components/data-list';
import { TuiOption } from '@taiga-ui/core/components/data-list';
import { TuiTextfieldDropdownDirective } from '@taiga-ui/core/components/textfield';
import { TuiBadgedContent, TuiBadge } from '@taiga-ui/kit';
import { flagsLG, LANGUAGES } from '../../const';
import { ObjLanguage } from '../../inteface';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { TelegramService } from '../../services/telegram.service';
import { environment } from '../../../environments/environment';

@Component({
  standalone: true,
  selector: 'app-settings',
  imports: [
    NgForOf,
    ReactiveFormsModule,
    TitleCasePipe,
    TuiButton,
    TuiButtonSelect,
    PolymorpheusTemplate,
    TuiBadgedContent,
    TuiBadge,
    TuiDataList,
    TuiFlagPipe,
    TuiOptGroup,
    TuiOption,
    TuiTextfield,
    TuiTextfieldDropdownDirective,
    TranslocoDirective,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [tuiScrollbarOptionsProvider({ mode: 'hover' })],
})
export class SettingsComponent implements OnInit {
  protected readonly icons = inject(TUI_DOC_ICONS);
  protected readonly switcher = inject(TuiLanguageSwitcherService);
  protected readonly language = new FormControl(
    capitalize(this.switcher.language),
  );
  public readonly userService = inject(UserService);
  public readonly translocoService = inject(TranslocoService);
  private readonly telegram = inject(TelegramService);
  private readonly alerts = inject(TuiAlertService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly window = inject(WA_WINDOW);

  public readonly flags = flagsLG;
  public readonly LANGUAGES = LANGUAGES;
  private readonly docsByLanguage: Record<
    string,
    { folder: string; privacy: string; terms: string }
  > = {
    ru: {
      folder: 'ru',
      privacy: 'Политика конфиденциальности Мои3эго.pdf',
      terms: 'Пользовательское соглашение Мои3эго.pdf',
    },
    gb: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    es: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    de: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    ar: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    zh: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    id: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    ja: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    tr: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
    pt: {
      folder: 'gb',
      privacy: 'Мои3эго Privacy Policy (EN).pdf',
      terms: 'Мои3эго Terms of Service (EN).pdf',
    },
  };
  @ViewChild('telegramNoticeTemplate')
  protected telegramNoticeTemplate?: TemplateRef<unknown>;

  ngOnInit(): void {
    const activeLang =
      this.userService.user.lang || this.translocoService.getActiveLang();
    const selected = this.LANGUAGES.find(
      (lang) => this.getLocaleFromLanguageKey(lang.key) === activeLang.toLowerCase(),
    );
    if (selected) {
      this.switcher.setLanguage(selected.title);
      this.language.setValue(capitalize(selected.title), {
        emitEvent: false,
      });
    }
  }

  public setLang(lang: ObjLanguage): void {
    const locale = this.getLocaleFromLanguageKey(lang.key);
    const translocoAny = this.translocoService as unknown as {
      load?: (lang: string) => { subscribe: (handlers: { next: () => void; error: () => void }) => void };
    };

    const applyLanguage = () => {
      this.translocoService.setActiveLang(locale);
      this.switcher.setLanguage(lang.title);
      const user = Object.assign({}, this.userService.user);
      user.lang = locale;
      user.synch = false;
      this.userService.user = user;
      this.userService.saveUser();
      this.language.setValue(capitalize(lang.title), { emitEvent: false });
      this.cdr.markForCheck();
    };

    if (typeof translocoAny.load === 'function') {
      translocoAny.load(locale).subscribe({
        next: applyLanguage,
        error: () => {
          // Do not switch active lang to avoid rendering raw transloco keys.
          this.alerts
            .open('Не удалось загрузить язык. Проверьте соединение и попробуйте снова.', {
              appearance: 'warning',
              autoClose: 3000,
            })
            .subscribe();
          this.cdr.markForCheck();
        },
      });
      return;
    }

    applyLanguage();
  }

  public supportWithStars(amount: number): boolean {
    if (!Number.isFinite(amount) || amount <= 0) {
      this.telegram.showAlert(
        this.translocoService.translate('settings.supportInvalidAmount'),
      );
      return false;
    }
    this.telegram.openStarsInvoiceByAmount(
      amount,
      this.translocoService.getActiveLang(),
    );
    return true;
  }

  public supportWithOneStar(): void {
    const tgUser = this.telegram.getTgUser();
    if (!tgUser) {
      this.showTelegramAlert();
      return;
    }
    this.supportWithStars(100);
  }

  public openTelegramBot(): void {
    this.window?.open(environment.telegramBotUrl, '_blank');
  }

  public get privacyPolicyUrl(): string {
    const docs = this.getDocsByLanguage();
    return this.buildDocUrl(docs.folder, docs.privacy);
  }

  public get termsOfServiceUrl(): string {
    const docs = this.getDocsByLanguage();
    return this.buildDocUrl(docs.folder, docs.terms);
  }

  private showTelegramAlert(): void {
    this.alerts
      .open(this.telegramNoticeTemplate || '', {
        appearance: 'info',
        autoClose: 0,
      })
      .subscribe();
  }

  private getActiveLanguage(): string {
    const current = this.translocoService.getActiveLang()?.toLowerCase();
    return this.docsByLanguage[current] ? current : 'ru';
  }

  private getLocaleFromLanguageKey(value: string): string {
    const normalized = value.toLowerCase();
    if (normalized === 'sa') return 'ar';
    if (normalized === 'cn') return 'zh';
    if (normalized === 'jp') return 'ja';
    return normalized;
  }

  private getDocsByLanguage(): { folder: string; privacy: string; terms: string } {
    return this.docsByLanguage[this.getActiveLanguage()];
  }

  private buildDocUrl(language: string, fileName: string): string {
    return (
      '/' +
      ['assets', 'i18n', language, 'landing', 'doc', fileName]
        .map((part) => encodeURIComponent(part))
        .join('/')
    );
  }

  // get translatedData() {
  //   return [
  //     {
  //       name: this.translocoService.translate('settings.testsCount'),
  //       user: 5,
  //       pro: 15,
  //     },
  //     {
  //       name: this.translocoService.translate('settings.aiRequests'),
  //       user: 5,
  //       pro: 50,
  //     },
  //     {
  //       name: this.translocoService.translate('settings.cost'),
  //       user: this.translocoService.translate('settings.free'),
  //       pro: this.translocoService.translate('settings.proCost'),
  //     },
  //   ] as const;
  // }
}

function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
